import type { FC } from "react";

import type { InviteSummary } from "@/pages/invites/invite.utils";
import { InvitePageViewKind } from "@/pages/invites/invite.types";
import { InviteLoadingView, InviteReadyView, InviteStatusView } from "@/pages/invites/views";

export interface InviteContentProps {
  viewKind: InvitePageViewKind;
  invite: InviteSummary | null;
  hasAccepted: boolean;
  isAccepting: boolean;
  acceptError: string | null;
  statusTitle: string | null;
  statusBody: string | null;
  successMessage: string | null;
  viewerName: string | null;
  onAccept: () => void;
  onOpen?: () => void;
  onDismiss?: () => void;
}

export const InviteContent: FC<InviteContentProps> = props => {
  const {
    viewKind,
    invite,
    hasAccepted,
    isAccepting,
    acceptError,
    statusTitle,
    statusBody,
    successMessage,
    viewerName,
    onAccept,
    onOpen,
    onDismiss,
  } = props;

  switch (viewKind) {
    case InvitePageViewKind.Loading:
      return <InviteLoadingView />;

    case InvitePageViewKind.Ready:
      if (!invite) {
        return null;
      }

      return (
        <InviteReadyView
          invite={invite}
          hasAccepted={hasAccepted}
          isAccepting={isAccepting}
          acceptError={acceptError}
          successMessage={successMessage ?? undefined}
          viewerName={viewerName}
          onAccept={onAccept}
          onOpen={onOpen}
        />
      );
    case InvitePageViewKind.Expired:
      return (
        <InviteStatusView
          title={statusTitle ?? "This invitation has expired."}
          description={statusBody}
          onAction={onDismiss}
        />
      );
    case InvitePageViewKind.AlreadyUsed:
      return (
        <InviteStatusView
          title={statusTitle ?? "This invitation has already been used."}
          description={statusBody}
          onAction={onDismiss}
        />
      );
    case InvitePageViewKind.NotFound:
      return (
        <InviteStatusView
          title={statusTitle ?? "We couldn't open this invitation."}
          description={statusBody}
          onAction={onDismiss}
        />
      );
    case InvitePageViewKind.Error:
      return (
        <InviteStatusView
          title={statusTitle ?? "We couldn't process this invitation."}
          description={statusBody ?? "Please try again."}
          onAction={onDismiss}
        />
      );
    default:
      return (
        <InviteStatusView
          title="We couldn't open this invitation."
          description={statusBody}
          onAction={onDismiss}
        />
      );
  }
};
